import { aiJson } from "@/services/ai.service";
import type { TopicSources } from "@/services/source.service";

/**
 * Klausur-Material aus dem Netz holen – **wörtlich**, nicht von der KI
 * nacherzählt.
 *
 * Warum das der ganze Punkt ist: Ein Gedicht, das ein Sprachmodell „aus dem
 * Gedächtnis" hinschreibt, hat verdrehte Zeilen, falsche Interpunktion und
 * gekürzte Strophen. Genau daran hängt aber jede Analyseaufgabe. Deshalb sucht
 * die KI hier nur den passenden TITEL, und der Text selbst wird per API
 * geholt und im Original übernommen.
 *
 * Quellen:
 *   Wikisource  – Primärtexte (Gedichte, Reden, Erzählungen), gemeinfrei
 *   Wikipedia   – Sachtexte, wenn kein Primärtext gesucht ist
 *
 * Beide APIs erlauben Zugriff aus dem Browser (`origin=*`), es braucht also
 * keinen Proxy und keinen Schlüssel.
 */

export interface WebSource {
  title: string;
  /** Der Originaltext als Markdown (Zeilenumbrüche wie im Original). */
  text: string;
  /** Quellenzeile, wie sie unter dem Material einer Klausur steht. */
  citation: string;
  url: string;
  kind: "wikisource" | "wikipedia";
}

interface Plan {
  kind?: string;
  query?: string;
  alternatives?: unknown;
  reason?: string;
}

/* ------------------------------ Suchauftrag ------------------------------ */

/**
 * Die KI schlägt vor, WONACH gesucht wird – mehr nicht. Der Vorschlag stützt
 * sich allein auf die Notizen des Nutzers, damit das Material zum Unterricht
 * passt und nicht zu irgendeinem verwandten Thema.
 */
export async function planSource(topicTitle: string, src: TopicSources, examType?: string): Promise<Plan> {
  return aiJson<Plan>(
    "Du suchst das Quellenmaterial für eine Klausur aus. Du schreibst KEINEN Text, du nennst nur " +
      "den Titel eines Werks, das per Wikisource geholt wird.\n\n" +
      "REGELN:\n" +
      "1. Es muss ein gemeinfreier PRIMÄRTEXT sein, den es auf de.wikisource.org gibt: " +
      "Gedicht, Ballade, Rede, Erzählung, historische Quelle, Gesetzestext, Brief.\n" +
      "2. NIMM NICHT die Werke, die im Material des Schülers behandelt werden – die kennt er schon. " +
      "Wähle ein ANDERES, aber vergleichbares Werk: gleiche Epoche, gleiche Gattung, gleiches Motiv, " +
      "gleicher Autor oder Zeitgenosse. Wenn im Unterricht drei Balladen dran waren, nimm eine vierte " +
      "bekannte Ballade derselben Zeit.\n" +
      "3. Erfinde keine Titel. Nenne nur Werke, die wirklich existieren und alt genug sind (vor 1930).\n" +
      "4. Passt zum Fach kein Primärtext (Mathematik, Informatik), nimm eine historische Quelle " +
      "oder Rede zum Sachgebiet.\n" +
      'query = "Titel Autor". alternatives = 2 weitere Titel als Ausweichvorschläge.\n' +
      'Gib NUR JSON zurück: {"kind":"wikisource","query":"…","alternatives":["…","…"],"reason":"ein Satz"}',
    `THEMA: ${topicTitle}${examType ? `\nPRÜFUNGSFORM: ${examType}` : ""}\n\nMATERIAL DES SCHÜLERS:\n${src.text.slice(0, 6000)}`,
    300
  );
}

/* -------------------------------- Abruf -------------------------------- */

const UA_PARAM = "&origin=*&format=json&formatversion=2";

async function api<T>(host: string, params: string, attempt = 0): Promise<T> {
  const res = await fetch(`https://${host}/w/api.php?${params}${UA_PARAM}`);
  // 429 heißt bei Wikimedia „zu schnell hintereinander", nicht „gesperrt".
  // Kurz warten und noch einmal fragen; zwei Anläufe reichen praktisch immer.
  if (res.status === 429 && attempt < 2) {
    await new Promise((r) => setTimeout(r, 1200 * (attempt + 1)));
    return api<T>(host, params, attempt + 1);
  }
  if (!res.ok) throw new Error(`${host} antwortet mit ${res.status}`);
  return (await res.json()) as T;
}

interface SearchReply {
  query?: { search?: { title: string }[] };
}

async function search(host: string, query: string): Promise<string[]> {
  const data = await api<SearchReply>(host, `action=query&list=search&srlimit=5&srsearch=${encodeURIComponent(query)}`);
  return (data.query?.search ?? []).map((s) => s.title);
}

/* ---------------------------- Wikisource ---------------------------- */

interface ParseReply {
  parse?: { title?: string; wikitext?: string; pageid?: number };
}

/** Werte aus der `{{Textdaten}}`-Box – daraus wird die Quellenzeile. */
function textdaten(wikitext: string): Record<string, string> {
  const start = wikitext.indexOf("{{Textdaten");
  if (start < 0) return {};
  // Bis zur schließenden Klammer der Box zählen, damit verschachtelte
  // Vorlagen darin (z. B. {{GBS|…}}) nicht zu früh abbrechen.
  let depth = 0;
  let end = start;
  for (let i = start; i < wikitext.length - 1; i++) {
    if (wikitext[i] === "{" && wikitext[i + 1] === "{") { depth++; i++; }
    else if (wikitext[i] === "}" && wikitext[i + 1] === "}") {
      depth--;
      i++;
      if (depth === 0) { end = i + 1; break; }
    }
  }
  const box = wikitext.slice(start, end);
  const out: Record<string, string> = {};
  for (const line of box.split("\n")) {
    const m = line.match(/^\s*\|\s*([A-ZÄÖÜa-zäöü]+)\s*=\s*(.*)$/);
    // Die Quellenzeile ist EINE Zeile – `<br />` in der Herkunftsangabe darf
    // sie nicht auseinanderreißen.
    if (m) out[m[1].toUpperCase()] = clean(m[2]).replace(/\s*\n\s*/g, " ").trim();
  }
  return out;
}

/**
 * Vorlagen `{{…}}` entfernen, auch verschachtelte und über mehrere Zeilen.
 * Zeilenweise geht das nicht: `{{Personendaten` steht allein in einer Zeile,
 * der Rest der Box in den folgenden – und landete sonst mitten im Werktext.
 */
function stripTemplates(s: string): string {
  let out = "";
  let depth = 0;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "{" && s[i + 1] === "{") { depth++; i++; continue; }
    if (s[i] === "}" && s[i + 1] === "}" && depth > 0) { depth--; i++; continue; }
    if (depth === 0) out += s[i];
  }
  return out;
}

/** Wikitext zu lesbarem Text: Links, Belege und Auszeichnungen entfernen. */
function clean(s: string): string {
  return stripTemplates(s)
    .replace(/<ref[^>]*>[\s\S]*?<\/ref>/g, "")
    .replace(/<ref[^>]*\/>/g, "")
    .replace(/\[\[(?:Kategorie|Datei|Bild|File|Image):[^\]]*\]\]/gi, "")
    .replace(/\[\[([^\]|]*\|)?([^\]]*)\]\]/g, "$2")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/'''''|'''|''/g, "")
    .replace(/[ \t]+/g, " ")
    .replace(/^ +| +$/gm, "");
}

/**
 * Der eigentliche Werktext. Steht er in `<poem>`, gilt genau das – dann sind
 * die Zeilenumbrüche bedeutungstragend und müssen erhalten bleiben.
 */
function bodyOf(wikitext: string): string {
  const poems = [...wikitext.matchAll(/<poem>([\s\S]*?)<\/poem>/gi)].map((m) => m[1]);
  const raw = poems.length ? poems.join("\n\n") : wikitext;
  return clean(raw).replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * Ist das überhaupt eine Werkseite? Autorenseiten und Werkverzeichnisse haben
 * viel Text, aber keinen Primärtext – als Klausurmaterial taugen sie nicht.
 * Verlässliches Kennzeichen auf de.wikisource ist die `{{Textdaten}}`-Box bzw.
 * ein `<poem>`-Block; Autorenseiten tragen stattdessen `{{Personendaten}}`.
 */
function isWorkPage(wikitext: string): boolean {
  if (/\{\{\s*Personendaten/i.test(wikitext)) return false;
  return /\{\{\s*Textdaten/i.test(wikitext) || /<poem>/i.test(wikitext);
}

/* --------------------- Fallback: gerenderte Seite --------------------- */

const ENTITIES: Record<string, string> = {
  amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ", shy: "",
};

function decodeEntities(s: string): string {
  return s
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(Number(dec)))
    .replace(/&([a-z]+);/gi, (m, name) => ENTITIES[name.toLowerCase()] ?? m);
}

/**
 * Viele Wikisource-Werke stehen nicht im Wikitext der Seite, sondern werden
 * seitenweise aus dem Scan-Namensraum eingebunden (`{{SeitePR|…}}`). Im
 * Wikitext steht dann nur das Gerüst. Deshalb wird als zweiter Anlauf die
 * FERTIG GERENDERTE Seite geholt und zu Text gemacht.
 *
 * Reguläre Ausdrücke auf HTML sind sonst eine schlechte Idee; hier ist die
 * Eingabe maschinell erzeugtes, gleichförmiges MediaWiki-Markup, und das
 * Ergebnis ist reiner Text, der nirgends wieder als HTML eingesetzt wird.
 */
function htmlToText(html: string): string {
  let s = html;

  // Alles wegwerfen, was Beiwerk ist: Metadaten-Tabelle, Fußnotenzeichen,
  // Seitenzahlen am Rand, Zeilennummern, Navigationsblöcke.
  s = s.replace(/<table[\s\S]*?<\/table>/gi, "");
  s = s.replace(/<sup\b[^>]*class="[^"]*reference[^"]*"[\s\S]*?<\/sup>/gi, "");
  s = s.replace(/<span\b[^>]*class="[^"]*(?:PageNumber|zeilennummer|cite-bracket)[^"]*"[\s\S]*?<\/span>/gi, "");
  s = s.replace(/<div\b[^>]*class="[^"]*ws-noexport[^"]*"[\s\S]*?<\/div>/gi, "");
  s = s.replace(/<ol\b[^>]*class="[^"]*references[^"]*"[\s\S]*?<\/ol>/gi, "");
  s = s.replace(/<style[\s\S]*?<\/style>|<script[\s\S]*?<\/script>/gi, "");

  // Der Werktext steht bei Gedichten und Balladen in `<div class="poem">`.
  const poems = [...s.matchAll(/<div\b[^>]*class="[^"]*poem[^"]*"[^>]*>([\s\S]*?)<\/div>\s*(?=<div|<p|$)/gi)]
    .map((m) => m[1]);
  const isPoem = poems.length > 0;
  if (isPoem) s = poems.join("\n\n");

  // Der Umbruch steckt im Tag, nicht im Quelltext: MediaWiki setzt hinter
  // jedes `<br />` noch einen echten Zeilenumbruch. Beides zusammen ergäbe
  // eine Leerzeile nach JEDEM Vers – deshalb schluckt der Ersatz die
  // nachfolgenden Leerzeichen gleich mit.
  // Im Vers beendet `</p>` nur die Strophe, in Prosa den Absatz.
  s = s
    .replace(/<br\s*\/?>\s*/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6]|tr)>\s*/gi, isPoem ? "\n" : "\n\n")
    .replace(/<[^>]+>/g, "");

  return decodeEntities(s)
    .replace(/[ \t ]+/g, " ")
    .replace(/^ +| +$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

interface HtmlReply {
  parse?: { title?: string; text?: string };
}

async function renderedText(host: string, title: string): Promise<string> {
  const data = await api<HtmlReply>(host, `action=parse&prop=text&redirects=1&page=${encodeURIComponent(title)}`);
  return htmlToText(data.parse?.text ?? "");
}

async function fromWikisource(query: string): Promise<WebSource> {
  const host = "de.wikisource.org";
  const found = await search(host, query).catch(() => [] as string[]);
  // Der Suchbegriff selbst zuerst: „Die Bürgschaft" ist auf Wikisource der
  // exakte Seitentitel, und ein direkter Treffer schlägt jede Volltextsuche.
  // Der Autorenname wird für den zweiten Versuch abgeschnitten, weil Werktitel
  // ihn nicht enthalten.
  const bare = query.replace(/\s+(von\s+)?[A-ZÄÖÜ][a-zäöüß]+$/, "").trim();
  const titles = [...new Set([query, bare, ...found])].filter(Boolean);

  let last = "";
  for (const title of titles) {
    try {
      const data = await api<ParseReply>(host, `action=parse&prop=wikitext&redirects=1&page=${encodeURIComponent(title)}`);
      const wikitext = data.parse?.wikitext ?? "";
      if (!isWorkPage(wikitext)) { last = title; continue; }

      let text = bodyOf(wikitext);
      // Steht der Werktext nicht im Wikitext (Seiten-Einbindung aus dem
      // Scan-Namensraum), liefert erst die gerenderte Seite etwas.
      if (words(text) < 40) text = await renderedText(host, title).catch(() => "");
      // Immer noch zu kurz heißt: Sammelseite oder Fragment.
      if (words(text) < 40) { last = title; continue; }

      const meta = textdaten(wikitext);
      const author = meta.AUTOR || "unbekannt";
      const year = meta.ERSCHEINUNGSJAHR || meta.ENTSTEHUNGSJAHR || "";
      const origin = meta.HERKUNFT ? `, ${meta.HERKUNFT}` : "";
      const url = `https://${host}/wiki/${encodeURIComponent((data.parse?.title ?? title).replace(/ /g, "_"))}`;

      return {
        kind: "wikisource",
        title: meta.TITEL || data.parse?.title || title,
        text: cut(text, 2200),
        citation:
          `${author}: ${meta.TITEL || title}${year ? ` (${year})` : ""}${origin}. ` +
          `Zitiert nach Wikisource, abgerufen am ${new Date().toLocaleDateString("de-DE")}.`,
        url,
      };
    } catch {
      last = title;
    }
  }
  throw new Error(`Kein brauchbarer Text auf Wikisource (zuletzt geprüft: ${last || query}).`);
}

/* ---------------------------- Wikipedia ---------------------------- */

interface ExtractReply {
  query?: { pages?: { title?: string; extract?: string }[] };
}

async function fromWikipedia(query: string): Promise<WebSource> {
  const host = "de.wikipedia.org";
  const titles = await search(host, query);
  const title = titles[0] ?? query;
  const data = await api<ExtractReply>(
    host,
    `action=query&prop=extracts&explaintext=1&redirects=1&titles=${encodeURIComponent(title)}`
  );
  const page = data.query?.pages?.[0];
  const raw = (page?.extract ?? "").trim();
  if (words(raw) < 80) throw new Error(`Zu „${query}" gibt es keinen brauchbaren Artikel.`);

  // Abschnittsüberschriften (== … ==) zu Markdown, Belegreste weg.
  const text = raw
    .replace(/^=+\s*(.+?)\s*=+$/gm, "\n**$1**\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  const url = `https://${host}/wiki/${encodeURIComponent((page?.title ?? title).replace(/ /g, "_"))}`;
  return {
    kind: "wikipedia",
    title: page?.title ?? title,
    text: cut(text, 2600),
    citation: `Artikel „${page?.title ?? title}", Wikipedia, abgerufen am ${new Date().toLocaleDateString("de-DE")}. ${url}`,
    url,
  };
}

/** Wortzahl – die Messlatte für „taugt als Klausurmaterial". */
function words(s: string): number {
  return s.split(/\s+/).filter(Boolean).length;
}

/** Kürzen, aber nur an Absatz- oder Strophengrenzen – nie mitten im Satz. */
function cut(text: string, max: number): string {
  if (text.length <= max) return text;
  const blocks = text.split(/\n\s*\n/);
  const out: string[] = [];
  let total = 0;
  for (const b of blocks) {
    if (total + b.length > max && out.length) break;
    out.push(b);
    total += b.length + 2;
  }
  return out.join("\n\n") + "\n\n[…]";
}

/* ------------------------------ Öffentlich ------------------------------ */

/**
 * Passendes Material zum Thema besorgen. Schlägt der Plan fehl oder liefert
 * die gewählte Quelle nichts, wird die jeweils andere versucht – erst danach
 * gibt es einen Fehler, den der Aufrufer dem Nutzer zeigen kann.
 */
export async function fetchExamSource(
  topicTitle: string,
  src: TopicSources,
  examType?: string
): Promise<WebSource> {
  let plan: Plan = {};
  try { plan = await planSource(topicTitle, src, examType); } catch { plan = {}; }

  const alts = Array.isArray(plan.alternatives) ? plan.alternatives.map((x) => String(x).trim()) : [];
  const queries = [...new Set([(plan.query ?? "").trim(), ...alts, topicTitle])].filter(Boolean);

  let lastErr: Error | null = null;
  for (const q of queries) {
    try { return await fromWikisource(q); } catch (err) { lastErr = err as Error; }
  }
  for (const q of queries) {
    try { return await fromWikipedia(q); } catch (err) { lastErr = err as Error; }
  }
  throw lastErr ?? new Error("Es ließ sich kein Material aus dem Netz holen.");
}
